import { useEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Checkbox,
  Collapse,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { getSessionDrawerChrome } from '@kangaroo/ui';

import type { SessionListItem } from '../../features/sessions/sessionList';
import { buildSessionSelectionTree, makeSessionPrimary, updateSessionSelection } from '../../features/sessions/sessionSelectionTree';

export {
  buildSessionSelectionTree,
  makeSessionPrimary,
  updateSessionSelection,
} from '../../features/sessions/sessionSelectionTree';
export type {
  SessionSelectionTreeItem,
  SessionSelectionTreeSession,
  SessionSelectionTreeDriverGroup,
  SessionSelectionTreeDateGroup,
} from '../../features/sessions/sessionSelectionTree';

function sessionMeta(session: SessionListItem) {
  const parts = [
    session.trackName?.trim() || 'Circuit not assigned',
    typeof session.lapCount === 'number' ? `${session.lapCount} laps` : null,
  ].filter(Boolean);
  return parts.join(' · ');
}

function driverInitials(label: string) {
  if (label === 'Driver unassigned') return '?';
  return label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toLocaleUpperCase())
    .join('');
}

export function SessionSelectionTree({
  sessions,
  primarySessionId,
  compareSessionIds,
  onSelectionChange,
}: {
  sessions: SessionListItem[];
  primarySessionId: string | null;
  compareSessionIds: string[];
  onSelectionChange: (sessionIds: string[]) => void;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const chrome = getSessionDrawerChrome(isDark);
  const groups = useMemo(() => buildSessionSelectionTree(sessions), [sessions]);
  const selectedIds = useMemo(() => {
    const ids = [primarySessionId, ...compareSessionIds].filter((id): id is string => Boolean(id));
    return [...new Set(ids)];
  }, [compareSessionIds, primarySessionId]);
  const primaryLocation = useMemo(() => {
    for (const date of groups) {
      for (const driver of date.drivers) {
        if (driver.sessions.some(({ session }) => session.id === primarySessionId)) {
          return { dateKey: date.key, driverKey: driver.key };
        }
      }
    }
    return null;
  }, [groups, primarySessionId]);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(() => new Set());
  const [expandedDrivers, setExpandedDrivers] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const dateKey = primaryLocation?.dateKey ?? groups[0]?.key;
    const driverKey = primaryLocation?.driverKey ?? groups[0]?.drivers[0]?.key;
    if (dateKey) {
      setExpandedDates((current) => current.has(dateKey) ? current : new Set(current).add(dateKey));
    }
    if (driverKey) {
      setExpandedDrivers((current) => current.has(driverKey) ? current : new Set(current).add(driverKey));
    }
  }, [groups, primaryLocation]);

  const toggleSetValue = (
    setter: Dispatch<SetStateAction<Set<string>>>,
    key: string,
  ) => {
    setter((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSession = (sessionId: string, checked: boolean) => {
    onSelectionChange(updateSessionSelection(selectedIds, sessionId, checked));
  };

  const makePrimary = (sessionId: string) => {
    if (sessionId === selectedIds[0]) return;
    onSelectionChange(makeSessionPrimary(selectedIds, sessionId));
  };

  return (
    <Box
      aria-label="Session folders"
      sx={{
        overflow: 'hidden',
        border: `1px solid ${chrome.cardBorder}`,
        borderRadius: 1.5,
        bgcolor: chrome.cardBg,
      }}
    >
      {groups.map((date, dateIndex) => {
        const dateExpanded = expandedDates.has(date.key);
        const sessionCount = date.drivers.reduce((total, driver) => total + driver.sessions.length, 0);
        return (
          <Box key={date.key} sx={{ borderTop: dateIndex > 0 ? `1px solid ${chrome.divider}` : 0 }}>
            <ButtonBase
              aria-expanded={dateExpanded}
              aria-controls={`session-date-${date.key}`}
              onClick={() => toggleSetValue(setExpandedDates, date.key)}
              sx={{ width: '100%', px: 1, py: 0.85, justifyContent: 'flex-start', textAlign: 'left' }}
            >
              <KeyboardArrowRightRoundedIcon
                sx={{
                  mr: 0.45,
                  fontSize: 19,
                  color: chrome.collapsibleIcon,
                  transform: dateExpanded ? 'rotate(90deg)' : 'none',
                  transition: 'transform 150ms ease',
                }}
              />
              <CalendarMonthRoundedIcon sx={{ mr: 0.75, fontSize: 17, color: '#8B5CF6' }} />
              <Typography sx={{ minWidth: 0, flex: 1, fontSize: '0.75rem', fontWeight: 850, color: chrome.title }} noWrap>
                {date.label}
              </Typography>
              <Typography sx={{ ml: 1, fontSize: '0.66rem', color: chrome.muted, fontVariantNumeric: 'tabular-nums' }}>
                {sessionCount}
              </Typography>
            </ButtonBase>

            <Collapse id={`session-date-${date.key}`} in={dateExpanded} timeout={160}>
              <Box sx={{ pb: 0.5 }}>
                {date.drivers.map((driver) => {
                  const driverExpanded = expandedDrivers.has(driver.key);
                  return (
                    <Box key={driver.key} sx={{ ml: 1.25, borderLeft: `1px solid ${chrome.divider}` }}>
                      <ButtonBase
                        aria-expanded={driverExpanded}
                        aria-controls={`session-driver-${driver.key}`}
                        onClick={() => toggleSetValue(setExpandedDrivers, driver.key)}
                        sx={{ width: '100%', px: 1, py: 0.65, justifyContent: 'flex-start', textAlign: 'left' }}
                      >
                        <KeyboardArrowRightRoundedIcon
                          sx={{
                            mr: 0.35,
                            fontSize: 18,
                            color: chrome.collapsibleIcon,
                            transform: driverExpanded ? 'rotate(90deg)' : 'none',
                            transition: 'transform 150ms ease',
                          }}
                        />
                        {driverExpanded
                          ? <FolderOpenRoundedIcon sx={{ mr: 0.65, fontSize: 17, color: '#A78BFA' }} />
                          : <FolderRoundedIcon sx={{ mr: 0.65, fontSize: 17, color: '#A78BFA' }} />}
                        <Avatar
                          src={driver.avatarUrl ?? undefined}
                          sx={{ mr: 0.7, width: 20, height: 20, bgcolor: 'rgba(139,92,246,0.16)', color: '#8B5CF6', fontSize: '0.58rem', fontWeight: 900 }}
                        >
                          {driverInitials(driver.label)}
                        </Avatar>
                        <Typography sx={{ minWidth: 0, flex: 1, fontSize: '0.72rem', fontWeight: 800, color: chrome.collapsibleTitle }} noWrap>
                          {driver.label}
                        </Typography>
                        <Typography sx={{ ml: 1, fontSize: '0.64rem', color: chrome.muted, fontVariantNumeric: 'tabular-nums' }}>
                          {driver.sessions.length}
                        </Typography>
                      </ButtonBase>

                      <Collapse id={`session-driver-${driver.key}`} in={driverExpanded} timeout={150}>
                        <Box sx={{ ml: 2.3, mr: 0.5, mb: 0.45, borderLeft: `1px solid ${chrome.divider}` }}>
                          {driver.sessions.map(({ session, timeLabel }) => {
                            const selectionIndex = selectedIds.indexOf(session.id);
                            const selected = selectionIndex >= 0;
                            const primary = selectionIndex === 0;
                            const selectionLabel = primary
                              ? 'Primary'
                              : selectionIndex > 0
                                ? `Compare ${selectionIndex}`
                                : null;
                            return (
                              <Stack
                                key={session.id}
                                direction="row"
                                alignItems="stretch"
                                sx={{
                                  minHeight: 48,
                                  borderTop: `1px solid ${chrome.tableRowBorder}`,
                                  bgcolor: selected ? 'rgba(124,58,237,0.08)' : 'transparent',
                                  '&:first-of-type': { borderTop: 0 },
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={selected}
                                  disabled={primary && selectedIds.length === 1}
                                  onChange={(event) => toggleSession(session.id, event.target.checked)}
                                  inputProps={{
                                    'aria-label': selected
                                      ? `Remove ${timeLabel} from session comparison`
                                      : `Add ${timeLabel} to session comparison`,
                                  }}
                                  sx={{
                                    alignSelf: 'center',
                                    ml: 0.25,
                                    mr: 0.1,
                                    p: 0.55,
                                    color: chrome.muted,
                                    '&.Mui-checked': { color: '#8B5CF6' },
                                  }}
                                />
                                <ButtonBase
                                  aria-label={`Make ${timeLabel} for ${driver.label} the primary session`}
                                  onClick={() => makePrimary(session.id)}
                                  sx={{
                                    minWidth: 0,
                                    flex: 1,
                                    px: 0.6,
                                    py: 0.65,
                                    justifyContent: 'flex-start',
                                    textAlign: 'left',
                                    borderRadius: 1,
                                  }}
                                >
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Stack direction="row" spacing={0.75} alignItems="baseline">
                                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 850, color: chrome.title, fontVariantNumeric: 'tabular-nums' }}>
                                        {timeLabel}
                                      </Typography>
                                      {selectionLabel ? (
                                        <Typography
                                          sx={{
                                            fontSize: '0.6rem',
                                            fontWeight: 900,
                                            color: primary ? '#8B5CF6' : chrome.sectionLabel,
                                            letterSpacing: '0.035em',
                                            textTransform: 'uppercase',
                                          }}
                                        >
                                          {selectionLabel}
                                        </Typography>
                                      ) : null}
                                    </Stack>
                                    <Typography sx={{ mt: 0.1, fontSize: '0.64rem', color: chrome.muted }} noWrap>
                                      {sessionMeta(session)}
                                    </Typography>
                                  </Box>
                                </ButtonBase>
                              </Stack>
                            );
                          })}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}
